import { jpegDisplaySize } from "./jpeg";
import { isWireError, pumpPreview } from "./previewPump";
import { PREVIEW_HELLO_PART_KIND, ROUTES } from "./protocol";
import {
  BridgeError,
  type BridgeErrorCode,
  type CameraInfo,
  type CameraStatus,
  type CaptureResult,
  type PreviewFrame,
  type PropertyDescriptor,
  type PropertyValue,
  type RecordResult,
  type RemoteCameraBridge,
} from "./types";

function displaySize(
  bytes: Uint8Array,
  code: BridgeErrorCode,
  what: string,
): { width: number; height: number } {
  try {
    return jpegDisplaySize(bytes);
  } catch (err) {
    throw new BridgeError(code, `${what} is not a readable JPEG`, err);
  }
}

function bridgeFailed(path: string, status: number): BridgeError {
  return new BridgeError("BridgeFailed", `the bridge answered HTTP ${status} to ${path}`);
}

/** A re-open can outrun the bridge's teardown of the stream just abandoned: the write
 *  loop notices a dead client only on a failed write, so the claim waits once. */
const PREVIEW_REOPEN_RETRY_MS = 100;

export class HttpBridge implements RemoteCameraBridge {
  constructor(
    private baseUrl: string,
    private timeoutMs = 10_000,
  ) {}

  // Reassigned by every startPreview() call rather than shared, so a superseded
  // stream's belated end can't be read against a newer generator's reset.
  // The boolean says whether a real stream was stopped, which is what tells the
  // next startPreview that a 409 is its own teardown rather than a stranger's stream.
  private stopActivePreview: () => Promise<boolean> = async () => false;
  private cameraId: string | null = null;

  private bound(): string {
    if (this.cameraId === null) throw new BridgeError("NotConnected", "no camera session");
    return this.cameraId;
  }

  // A backgrounded phone accepts the connection and then answers nothing, so an
  // unbounded fetch hangs forever.
  private async fetchBounded(
    path: string,
    init: RequestInit = {},
    controller = new AbortController(),
  ): Promise<Response> {
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      return await fetch(`${this.baseUrl}${path}`, { ...init, signal: controller.signal });
    } catch (err) {
      if (controller.signal.aborted) {
        throw new BridgeError("Timeout", `${path} did not respond within ${this.timeoutMs}ms`);
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }

  // The preview stream's counterpart to fetchBounded: a bridge that answers the request
  // and then goes quiet would otherwise leave startPreview() pending forever.
  private async awaitBounded<T>(promise: Promise<T>, what: string): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        promise,
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new BridgeError("Timeout", `${what} within ${this.timeoutMs}ms`)),
            this.timeoutMs,
          );
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  }

  private async request(
    path: string,
    init?: RequestInit,
    controller?: AbortController,
    stream?: false,
  ): Promise<{ res: Response; body: Uint8Array<ArrayBuffer> }>;
  private async request(
    path: string,
    init: RequestInit,
    controller: AbortController | undefined,
    stream: true,
  ): Promise<{ res: Response }>;
  private async request(
    path: string,
    init: RequestInit = {},
    controller?: AbortController,
    stream = false,
  ): Promise<{ res: Response; body?: Uint8Array<ArrayBuffer> }> {
    const res = await this.fetchBounded(path, init, controller);
    if (!res.ok) {
      const errorBody = new Uint8Array(await res.arrayBuffer());
      let parsed: unknown;
      try {
        parsed = JSON.parse(new TextDecoder().decode(errorBody));
      } catch {
        parsed = undefined;
      }
      if (isWireError(parsed)) {
        throw new BridgeError(parsed.code, parsed.message, parsed.detail);
      }
      throw bridgeFailed(path, res.status);
    }
    if (stream) return { res };
    return { res, body: new Uint8Array(await res.arrayBuffer()) };
  }

  async listCameras(): Promise<CameraInfo[]> {
    const { body } = await this.request(ROUTES.cameras);
    return JSON.parse(new TextDecoder().decode(body)) as CameraInfo[];
  }

  async connect(cameraId: string): Promise<void> {
    // Bound before the answer: a bridge that binds the camera and then answers past
    // the client timeout still has to be releasable, or it stays bound forever.
    this.cameraId = cameraId;
    await this.request(ROUTES.connect(cameraId), { method: "POST" });
  }

  async disconnect(): Promise<void> {
    const id = this.bound();
    // Releasing the camera makes the bridge end the preview itself, which the client
    // would otherwise decode as a preview it lost.
    await this.stopActivePreview();
    await this.request(ROUTES.disconnect(id), { method: "POST" });
    this.cameraId = null;
  }

  async getStatus(): Promise<CameraStatus> {
    const { body } = await this.request(ROUTES.status(this.bound()));
    return JSON.parse(new TextDecoder().decode(body)) as CameraStatus;
  }

  async listProperties(): Promise<PropertyDescriptor[]> {
    const { body } = await this.request(ROUTES.properties(this.bound()));
    return JSON.parse(new TextDecoder().decode(body)) as PropertyDescriptor[];
  }

  async setProperty(name: string, value: PropertyValue): Promise<PropertyDescriptor[]> {
    const { body } = await this.request(ROUTES.properties(this.bound()), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, value }),
    });
    return JSON.parse(new TextDecoder().decode(body)) as PropertyDescriptor[];
  }

  async capture(): Promise<CaptureResult> {
    const path = ROUTES.capture(this.bound());
    const { body } = await this.request(path, { method: "POST" });
    const { width, height } = displaySize(body, "CaptureFailed", `capture from ${path}`);
    return { blob: new Blob([body], { type: "image/jpeg" }), width, height };
  }

  async *startPreview(signal?: AbortSignal): AsyncIterable<PreviewFrame> {
    const path = ROUTES.preview(this.bound());
    const stoppedPrevious = await this.stopActivePreview();
    if (signal?.aborted) return;
    let closingIntentionally = false;
    const controller = new AbortController();
    // Set once the response body's reader exists; stop() cancels it directly rather than
    // waiting for a paused consumer to resume the generator and reach its own cleanup.
    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
    const stop = async (): Promise<boolean> => {
      closingIntentionally = true;
      controller.abort();
      await reader?.cancel().catch(() => {});
      return true;
    };
    const onAbort = () => void stop();
    signal?.addEventListener("abort", onAbort, { once: true });
    this.stopActivePreview = stop;
    try {
      let res: Response;
      try {
        ({ res } = await this.request(path, {}, controller, true));
      } catch (err) {
        if (closingIntentionally) return;
        // A 409 right after abandoning our own stream is the bridge still tearing the
        // old one down; any other conflict is a stranger's live stream and stays final.
        if (!(stoppedPrevious && err instanceof BridgeError && err.code === "PreviewLost")) {
          throw err;
        }
        await new Promise((resolve) => setTimeout(resolve, PREVIEW_REOPEN_RETRY_MS));
        if (signal?.aborted || closingIntentionally) return;
        ({ res } = await this.request(path, {}, controller, true));
      }
      if (closingIntentionally) return;
      const body = res.body;
      if (!body) throw new BridgeError("PreviewLost", `${path} answered with no body`);
      reader = body.getReader();

      const parts = pumpPreview(reader, path, () => closingIntentionally)[Symbol.asyncIterator]();
      // The first part rides its own bound so a bridge that opens the stream and then
      // goes silent fails as a timeout instead of hanging the generator.
      const first = await this.awaitBounded(parts.next(), `${path} sent no part`);
      if (first.done) {
        if (closingIntentionally) return;
        throw new BridgeError("PreviewLost", `${path} ended before its first part`);
      }

      try {
        let part = first.value;
        while (true) {
          if (part.kind !== PREVIEW_HELLO_PART_KIND) {
            yield {
              data: new Blob([part.bytes]),
              ...displaySize(part.bytes, "PreviewLost", "preview frame"),
            };
          }
          const next = await this.awaitBounded(parts.next(), `${path} sent no preview frame`);
          if (next.done) return;
          part = next.value;
        }
      } finally {
        closingIntentionally = true;
        controller.abort();
        await parts.return?.().catch(() => {});
      }
    } finally {
      signal?.removeEventListener("abort", onAbort);
    }
  }

  async startRecording(): Promise<void> {
    this.bound();
    throw new BridgeError("RecordFailed", "recording has no wire route yet");
  }

  async stopRecording(): Promise<RecordResult> {
    this.bound();
    throw new BridgeError("RecordFailed", "recording has no wire route yet");
  }
}
